import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, HeartHandshake, Award, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useData } from "../DataContext";
import { usePageMeta } from "../hooks/usePageMeta";
import JsonLd from "../components/JsonLd";
import { buildWebPageLD, buildBreadcrumbLD } from "../lib/jsonld";

export default function About() {
  usePageMeta(
    "About Us — Our Journey Since 2006",
    "Founded in Kolkata in 2006, BAISHALII has grown into a trusted manufacturer of water tanks, pipes & fittings, sinks, cisterns and taps serving homes, farms and infrastructure across India."
  );
  const { STATS, TIMELINE } = useData();
  return (
    <div className="pt-[88px] md:pt-[112px]">
      <JsonLd blocks={[
        buildWebPageLD({ name: "About BAISHALII", path: "/about", description: "Founded in Kolkata in 2006 — the BAISHALII story, mission and milestones." }),
        buildBreadcrumbLD([{ name: "Home", path: "/" }, { name: "About Us", path: "/about" }]),
      ]} id="about-page" />
      {/* page hero */}
      <section className="relative page-hero-h min-h-[420px] flex items-center bg-slate-900 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1513257805917-a0da1146eb15" alt="about" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#061a3d] via-[#0a2a5e]/85 to-[#0a2a5e]/40" />
        <div className="relative max-w-7xl mx-auto px-6 text-white">
          <div className="text-sky-300 font-semibold text-sm tracking-[0.2em] mb-3">ABOUT US</div>
          <h1 className="text-5xl md:text-6xl font-black mb-4">Engineering trust, since 2006</h1>
          <p className="text-lg text-slate-200 max-w-2xl">From stainless steel sinks in Kolkata to a full range of plumbing solutions — the BAISHALII story is built on innovation, integrity and uncompromising quality.</p>
        </div>
      </section>

      {/* story */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1513828742140-ccaa28f3eda0" alt="factory" className="rounded-2xl shadow-xl w-full aspect-[4/5] object-cover" />
            <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-sky-500 to-[#0a2a5e] text-white p-6 rounded-2xl shadow-xl hidden md:block">
              <div className="text-4xl font-black">20+</div>
              <div className="text-sm">Years of Excellence</div>
            </div>
          </div>
          <div>
            <div className="text-sky-600 font-semibold text-sm tracking-[0.2em] mb-3">OUR STORY</div>
            <h2 className="text-4xl md:text-5xl font-black text-[#0a2a5e] mb-6 leading-tight">A legacy of pioneering piping innovation</h2>
            <p className="text-slate-600 text-base mb-4 leading-relaxed">Founded in 2006 in Kolkata, BAISHALII began its journey manufacturing stainless steel kitchen sinks — before expanding into cisterns, seat covers, water storage tanks, household products, PVC garden pipes, and a comprehensive range of uPVC, SWR and Agricultural Pipes &amp; Fittings.</p>
            <p className="text-slate-600 text-base mb-4 leading-relaxed">As the first ISI-certified Stainless Steel Sink manufacturer in West Bengal and Eastern India, we have built a reputation for quality, innovation and reliability — today offering 500+ SKUs through a growing network of dealers and plumbers across India.</p>
            <p className="text-slate-600 text-base mb-8 leading-relaxed">Our 25-year warranty isn't just a promise — it's a guarantee built into every metre of pipe that leaves our plants.</p>
            <div className="grid grid-cols-2 gap-4">
              {STATS.map((s, i) => (
                <div key={i} className="p-5 rounded-xl bg-sky-50 border border-sky-100">
                  <div className="text-3xl font-black text-[#0a2a5e]">{s.value}</div>
                  <div className="text-xs text-slate-600 font-medium">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* vision mission */}
      <section id="vision" className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-sky-600 font-semibold text-sm tracking-[0.2em] mb-3">VISION &amp; VALUES</div>
            <h2 className="text-4xl md:text-5xl font-black text-[#0a2a5e]">What drives us forward</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { Icon: Eye, title: "Our Vision", text: "To be the most preferred piping brand globally — known for innovation, quality and trust." },
              { Icon: Target, title: "Our Mission", text: "To deliver world-class piping solutions that improve lives, conserve water and last generations." },
              { Icon: HeartHandshake, title: "Our Values", text: "Integrity, innovation, sustainability and customer-first thinking guide every decision we make." }
            ].map(({ Icon, title, text }, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-sky-500 to-[#0a2a5e] flex items-center justify-center mb-5">
                  <Icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-black text-[#0a2a5e] mb-3">{title}</h3>
                <p className="text-slate-600 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* timeline */}
      <section id="timeline" className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-sky-600 font-semibold text-sm tracking-[0.2em] mb-3">OUR JOURNEY</div>
            <h2 className="text-4xl md:text-5xl font-black text-[#0a2a5e]">Milestones that shaped us</h2>
          </div>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-sky-300 via-sky-500 to-[#0a2a5e]" />
            <div className="space-y-10">
              {TIMELINE.map((m, i) => (
                <div key={i} className={`relative flex items-center gap-6 md:gap-0 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                  <div className="hidden md:block w-1/2" />
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 h-5 w-5 rounded-full bg-gradient-to-br from-sky-400 to-[#0a2a5e] ring-4 ring-white shadow-md z-10" />
                  <div className="ml-12 md:ml-0 md:w-1/2 md:px-10">
                    <div className="bg-gradient-to-br from-sky-50 to-white p-6 rounded-2xl border border-sky-100 hover:shadow-lg transition-shadow">
                      <div className="text-2xl font-black bg-gradient-to-r from-sky-600 to-[#0a2a5e] bg-clip-text text-transparent">{m.year}</div>
                      <div className="font-bold text-[#0a2a5e] text-lg mt-1">{m.title}</div>
                      <p className="text-slate-600 text-sm mt-2 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* warranty */}
      <section id="warranty" className="py-20 md:py-24 bg-gradient-to-r from-[#061a3d] via-[#0a2a5e] to-sky-700 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-500/20 border border-sky-400/40 text-sky-200 text-xs font-semibold tracking-wider mb-5">
              <Award className="h-3.5 w-3.5" /> INDUSTRY FIRST
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-5">25-Year Warranty on flagship products</h2>
            <p className="text-slate-200 text-lg mb-8 max-w-lg">We stand behind every metre of pipe we make. Our Flagship CPVC PowerGuard Pipes &amp; Fittings are ISI certified and come with an industry-leading 25-year manufacturer warranty.</p>
            <Button asChild size="lg" className="bg-white text-[#0a2a5e] hover:bg-sky-50 rounded-full font-bold"><Link to="/contact">Download Warranty Document <ArrowRight className="h-4 w-4 ml-1.5" /></Link></Button>
          </div>
          <div className="flex justify-center">
            <div className="h-64 w-64 md:h-80 md:w-80 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center shadow-2xl ring-8 ring-white/10">
              <div className="text-center">
                <div className="text-7xl md:text-8xl font-black text-white">25</div>
                <div className="text-sky-100 font-bold tracking-widest">YEAR WARRANTY</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
